import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  User,
  Home,
  Thermometer,
  ClipboardList,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Ruler,
  Wind,
  Flame,
  DoorOpen
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STEPS = [
  { id: 1, title: "Informations client", icon: User },
  { id: 2, title: "Besoins client", icon: ClipboardList },
  { id: 3, title: "Audit general", icon: Home },
  { id: 4, title: "Enveloppe batiment", icon: Ruler },
  { id: 5, title: "Equipements", icon: Thermometer },
  { id: 6, title: "Confirmation", icon: CheckCircle },
];

const INITIAL = {
  nom: "", prenom: "", telephone: "", email: "", adresse: "", surface: "", budget: "", travaux_envisages: "",
  besoins_client: "",
  surface_habitable: "", hauteur_plafond: "", annee_construction: "", occupants: "",
  etat_general: "moyen",
  isolation_combles: "", acces_combles: "non", humidite_combles: "", etat_toiture: "moyen",
  isolation_murs_ext: "non", fissures_ext: "non", etat_murs: "moyen",
  isolation_murs_int: "non", humidite_int: "non",
  nombre_fenetres: "", type_vitrage: "double", pourcentage_simple: "", etancheite_joints: "ok", etat_menuiseries: "moyen",
  type_chauffage: "", consommation_kwh: "", cout_annuel: "", rendement: "ok", etat_chauffage: "moyen",
  type_ventilation: "", humidite_moyenne: "", fonctionnement_vmc: "ok", etat_ventilation: "moyen",
  isolation_sol: "non", froid_ressenti: "non", etat_sol: "moyen",
  notes_observations: ""
};

function EtatBMC({ value, onChange, testId }) {
  const opts = [
    { val: "bon", label: "Bon", cls: "bg-emerald-500 text-white", ring: "ring-emerald-300" },
    { val: "moyen", label: "Moyen", cls: "bg-amber-500 text-white", ring: "ring-amber-300" },
    { val: "critique", label: "Critique", cls: "bg-red-500 text-white", ring: "ring-red-300" },
  ];
  return (
    <div className="flex gap-1.5">
      {opts.map((o) => (
        <button
          key={o.val}
          type="button"
          onClick={() => onChange(o.val)}
          data-testid={testId ? `${testId}-${o.val}` : undefined}
          className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
            value === o.val ? `${o.cls} ring-2 ${o.ring}` : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function OuiNon({ value, onChange, testId }) {
  return (
    <div className="flex gap-1.5">
      {[{ v: "oui", l: "Oui" }, { v: "non", l: "Non" }].map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          data-testid={testId ? `${testId}-${o.v}` : undefined}
          className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
            value === o.v
              ? o.v === "oui" ? "bg-emerald-500 text-white ring-2 ring-emerald-300" : "bg-slate-600 text-white ring-2 ring-slate-300"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function AuditRow({ label, children, icon: Icon }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 sm:w-48 flex-shrink-0">
        {Icon && <Icon size={14} className="text-slate-500" />}
        <span className="text-sm font-medium text-gray-800">{label}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3 flex-1">{children}</div>
    </div>
  );
}

export default function ExpertPlanForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: null }));
  };

  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.nom) e.nom = "Requis";
      if (!form.prenom) e.prenom = "Requis";
      if (!form.email) e.email = "Requis";
      if (!form.adresse) e.adresse = "Requis";
    }
    if (s === 6 && !form.nom) e.confirm = "Formulaire incomplet";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step) && step < 6) setStep((s) => s + 1); };
  const prev = () => { if (step > 1) setStep((s) => s - 1); };

  const submit = async () => {
    if (!validate(6)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/expert-plan`, form, { timeout: 30000 });
      toast.success("Votre demande de rapport expert a ete envoyee avec succes !");
      setForm(INITIAL);
      setStep(1);
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez reessayer.");
    } finally {
      setLoading(false);
    }
  };

  const pct = (step / 6) * 100;
  const err = (f) => errors[f] ? "border-red-400" : "";

  return (
    <div data-testid="expert-plan-form">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-500">Etape {step} sur 6</span>
          <span className="text-sm font-semibold text-emerald-600">{Math.round(pct)}%</span>
        </div>
        <Progress value={pct} className="h-2.5 bg-gray-200" />
      </div>

      {/* Step indicators */}
      <div className="hidden md:flex justify-between mb-8">
        {STEPS.map((s) => (
          <div key={s.id} className={`flex flex-col items-center gap-1.5 ${
            s.id === step ? "text-emerald-600" : s.id < step ? "text-emerald-400" : "text-gray-300"
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              s.id === step ? "bg-emerald-600 text-white" : s.id < step ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-400"
            }`}>
              <s.icon size={18} />
            </div>
            <span className="text-[10px] text-center max-w-[70px] leading-tight">{s.title}</span>
          </div>
        ))}
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            className="p-6"
          >
            {/* Step 1: Client Info */}
            {step === 1 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <User size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Informations client</h3>
                    <p className="text-gray-500 text-xs">Vos coordonnees et votre projet</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm">Nom *</Label>
                    <Input value={form.nom} onChange={(e) => set("nom", e.target.value)} placeholder="Dupont" className={err("nom")} data-testid="audit-nom" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Prenom *</Label>
                    <Input value={form.prenom} onChange={(e) => set("prenom", e.target.value)} placeholder="Jean" className={err("prenom")} data-testid="audit-prenom" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Telephone</Label>
                    <Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} placeholder="06 12 34 56 78" data-testid="audit-tel" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@exemple.com" className={err("email")} data-testid="audit-email" />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-sm">Adresse du bien *</Label>
                    <Input value={form.adresse} onChange={(e) => set("adresse", e.target.value)} placeholder="123 Rue Exemple, 75001 Paris" className={err("adresse")} data-testid="audit-adresse" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Surface (m2)</Label>
                    <Input type="number" value={form.surface} onChange={(e) => set("surface", e.target.value)} placeholder="100" data-testid="audit-surface" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Budget estime (EUR)</Label>
                    <Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="15000" data-testid="audit-budget" />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-sm">Travaux envisages</Label>
                    <Input value={form.travaux_envisages} onChange={(e) => set("travaux_envisages", e.target.value)} placeholder="Isolation, chauffage, fenetres..." data-testid="audit-travaux" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Besoins */}
            {step === 2 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <ClipboardList size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Besoins client</h3>
                    <p className="text-gray-500 text-xs">Decrivez vos besoins et attentes pour le rapport final</p>
                  </div>
                </div>
                <textarea
                  value={form.besoins_client}
                  onChange={(e) => set("besoins_client", e.target.value)}
                  placeholder="Decrivez vos besoins en detail : objectifs de renovation, problemes constates, attentes particulieres..."
                  className="w-full h-48 p-4 border border-gray-200 rounded-xl resize-none text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="audit-besoins"
                />
              </div>
            )}

            {/* Step 3: Audit General */}
            {step === 3 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                    <Home size={20} className="text-slate-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Audit general</h3>
                    <p className="text-gray-500 text-xs">Informations generales du logement</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <AuditRow label="Surface habitable" icon={Ruler}>
                    <Input type="number" value={form.surface_habitable} onChange={(e) => set("surface_habitable", e.target.value)} placeholder="m2" className="w-24 h-8 text-sm" data-testid="audit-surface-hab" />
                    <EtatBMC value={form.etat_general} onChange={(v) => set("etat_general", v)} testId="etat-general" />
                  </AuditRow>
                  <AuditRow label="Hauteur plafond">
                    <Input type="number" step="0.1" value={form.hauteur_plafond} onChange={(e) => set("hauteur_plafond", e.target.value)} placeholder="m" className="w-24 h-8 text-sm" data-testid="audit-hauteur" />
                  </AuditRow>
                  <AuditRow label="Annee construction">
                    <Input type="number" value={form.annee_construction} onChange={(e) => set("annee_construction", e.target.value)} placeholder="1980" className="w-28 h-8 text-sm" data-testid="audit-annee" />
                  </AuditRow>
                  <AuditRow label="Occupants">
                    <Input type="number" value={form.occupants} onChange={(e) => set("occupants", e.target.value)} placeholder="Nb" className="w-20 h-8 text-sm" data-testid="audit-occupants" />
                  </AuditRow>
                </div>
              </div>
            )}

            {/* Step 4: Enveloppe */}
            {step === 4 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Ruler size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Enveloppe du batiment</h3>
                    <p className="text-gray-500 text-xs">Toiture, murs exterieurs et interieurs</p>
                  </div>
                </div>

                {/* Toiture */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Toiture / Combles</h4>
                  <AuditRow label="Isolation combles">
                    <Input value={form.isolation_combles} onChange={(e) => set("isolation_combles", e.target.value)} placeholder="epaisseur cm" className="w-28 h-8 text-sm" data-testid="audit-iso-combles" />
                    <EtatBMC value={form.etat_toiture} onChange={(v) => set("etat_toiture", v)} testId="etat-toiture" />
                  </AuditRow>
                  <AuditRow label="Acces combles">
                    <OuiNon value={form.acces_combles} onChange={(v) => set("acces_combles", v)} testId="acces-combles" />
                  </AuditRow>
                  <AuditRow label="Humidite combles">
                    <Input value={form.humidite_combles} onChange={(e) => set("humidite_combles", e.target.value)} placeholder="%" className="w-20 h-8 text-sm" data-testid="audit-hum-combles" />
                  </AuditRow>
                </div>

                {/* Murs Ext */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Murs exterieurs</h4>
                  <AuditRow label="Isolation">
                    <OuiNon value={form.isolation_murs_ext} onChange={(v) => set("isolation_murs_ext", v)} testId="iso-murs-ext" />
                    <EtatBMC value={form.etat_murs} onChange={(v) => set("etat_murs", v)} testId="etat-murs" />
                  </AuditRow>
                  <AuditRow label="Fissures ext">
                    <OuiNon value={form.fissures_ext} onChange={(v) => set("fissures_ext", v)} testId="fissures-ext" />
                  </AuditRow>
                </div>

                {/* Murs Int */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Murs interieurs</h4>
                  <AuditRow label="Isolation">
                    <OuiNon value={form.isolation_murs_int} onChange={(v) => set("isolation_murs_int", v)} testId="iso-murs-int" />
                  </AuditRow>
                  <AuditRow label="Humidite int">
                    <OuiNon value={form.humidite_int} onChange={(v) => set("humidite_int", v)} testId="hum-int" />
                  </AuditRow>
                </div>
              </div>
            )}

            {/* Step 5: Equipements */}
            {step === 5 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Thermometer size={20} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Equipements</h3>
                    <p className="text-gray-500 text-xs">Menuiseries, chauffage, ventilation et sol</p>
                  </div>
                </div>

                {/* Menuiseries */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Menuiseries</h4>
                  <AuditRow label="Nombre fenetres" icon={DoorOpen}>
                    <Input type="number" value={form.nombre_fenetres} onChange={(e) => set("nombre_fenetres", e.target.value)} placeholder="Nb" className="w-20 h-8 text-sm" data-testid="audit-nb-fenetres" />
                    <EtatBMC value={form.etat_menuiseries} onChange={(v) => set("etat_menuiseries", v)} testId="etat-menuiseries" />
                  </AuditRow>
                  <AuditRow label="Type vitrage">
                    <div className="flex gap-1.5">
                      {["simple", "double", "triple"].map((t) => (
                        <button key={t} type="button" onClick={() => set("type_vitrage", t)}
                          data-testid={`vitrage-${t}`}
                          className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
                            form.type_vitrage === t ? "bg-slate-700 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                      ))}
                    </div>
                  </AuditRow>
                  <AuditRow label="Simple vitrage %">
                    <Input value={form.pourcentage_simple} onChange={(e) => set("pourcentage_simple", e.target.value)} placeholder="%" className="w-20 h-8 text-sm" data-testid="audit-pct-simple" />
                  </AuditRow>
                  <AuditRow label="Etancheite joints">
                    <div className="flex gap-1.5">
                      {[{ v: "ok", l: "OK" }, { v: "hs", l: "HS" }].map((o) => (
                        <button key={o.v} type="button" onClick={() => set("etancheite_joints", o.v)}
                          data-testid={`joints-${o.v}`}
                          className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
                            form.etancheite_joints === o.v
                              ? o.v === "ok" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}>{o.l}</button>
                      ))}
                    </div>
                  </AuditRow>
                </div>

                {/* Chauffage */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Chauffage</h4>
                  <AuditRow label="Type" icon={Flame}>
                    <Input value={form.type_chauffage} onChange={(e) => set("type_chauffage", e.target.value)} placeholder="Gaz, elec, PAC..." className="w-36 h-8 text-sm" data-testid="audit-type-chauffage" />
                    <EtatBMC value={form.etat_chauffage} onChange={(v) => set("etat_chauffage", v)} testId="etat-chauffage" />
                  </AuditRow>
                  <AuditRow label="Consommation">
                    <Input type="number" value={form.consommation_kwh} onChange={(e) => set("consommation_kwh", e.target.value)} placeholder="kWh/an" className="w-28 h-8 text-sm" data-testid="audit-conso" />
                    <Input type="number" value={form.cout_annuel} onChange={(e) => set("cout_annuel", e.target.value)} placeholder="EUR/an" className="w-28 h-8 text-sm" data-testid="audit-cout" />
                  </AuditRow>
                  <AuditRow label="Rendement/Bruit">
                    <div className="flex gap-1.5">
                      {[{ v: "ok", l: "OK" }, { v: "mauvais", l: "Mauvais" }].map((o) => (
                        <button key={o.v} type="button" onClick={() => set("rendement", o.v)}
                          data-testid={`rendement-${o.v}`}
                          className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
                            form.rendement === o.v
                              ? o.v === "ok" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}>{o.l}</button>
                      ))}
                    </div>
                  </AuditRow>
                </div>

                {/* Ventilation */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Ventilation</h4>
                  <AuditRow label="Type" icon={Wind}>
                    <Input value={form.type_ventilation} onChange={(e) => set("type_ventilation", e.target.value)} placeholder="VMC, naturelle..." className="w-36 h-8 text-sm" data-testid="audit-type-vmc" />
                    <EtatBMC value={form.etat_ventilation} onChange={(v) => set("etat_ventilation", v)} testId="etat-ventilation" />
                  </AuditRow>
                  <AuditRow label="Humidite">
                    <Input value={form.humidite_moyenne} onChange={(e) => set("humidite_moyenne", e.target.value)} placeholder="%" className="w-20 h-8 text-sm" data-testid="audit-hum-moy" />
                  </AuditRow>
                  <AuditRow label="Fonctionnement">
                    <div className="flex gap-1.5">
                      {[{ v: "ok", l: "OK" }, { v: "hs", l: "HS" }, { v: "absent", l: "Absent" }].map((o) => (
                        <button key={o.v} type="button" onClick={() => set("fonctionnement_vmc", o.v)}
                          data-testid={`vmc-${o.v}`}
                          className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
                            form.fonctionnement_vmc === o.v
                              ? o.v === "ok" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}>{o.l}</button>
                      ))}
                    </div>
                  </AuditRow>
                </div>

                {/* Sol */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Sol</h4>
                  <AuditRow label="Isolation sol">
                    <OuiNon value={form.isolation_sol} onChange={(v) => set("isolation_sol", v)} testId="iso-sol" />
                    <EtatBMC value={form.etat_sol} onChange={(v) => set("etat_sol", v)} testId="etat-sol" />
                  </AuditRow>
                  <AuditRow label="Froid ressenti">
                    <OuiNon value={form.froid_ressenti} onChange={(v) => set("froid_ressenti", v)} testId="froid-ressenti" />
                  </AuditRow>
                </div>
              </div>
            )}

            {/* Step 6: Confirmation */}
            {step === 6 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <CheckCircle size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Confirmation et envoi</h3>
                    <p className="text-gray-500 text-xs">Verifiez vos informations et ajoutez des notes</p>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm mb-1">Client</h4>
                      <p className="text-gray-600 text-xs">{form.prenom} {form.nom}</p>
                      <p className="text-gray-500 text-xs">{form.email}</p>
                      <p className="text-gray-500 text-xs">{form.adresse}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm mb-1">Projet</h4>
                      <p className="text-gray-600 text-xs">Surface: {form.surface || form.surface_habitable || "-"} m2</p>
                      <p className="text-gray-500 text-xs">Budget: {form.budget || "-"} EUR</p>
                      <p className="text-gray-500 text-xs">Travaux: {form.travaux_envisages || "-"}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm mb-1">Chauffage</h4>
                      <p className="text-gray-600 text-xs">{form.type_chauffage || "-"} ({form.etat_chauffage})</p>
                      <p className="text-gray-500 text-xs">{form.consommation_kwh ? `${form.consommation_kwh} kWh/an` : "-"}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm mb-1">Ventilation</h4>
                      <p className="text-gray-600 text-xs">{form.type_ventilation || "-"} ({form.fonctionnement_vmc})</p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Notes / Observations supplementaires</Label>
                  <textarea
                    value={form.notes_observations}
                    onChange={(e) => set("notes_observations", e.target.value)}
                    placeholder="Ajoutez des observations, photos de reference, remarques particulieres..."
                    className="w-full h-32 p-4 border border-gray-200 rounded-xl resize-none text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="audit-notes"
                  />
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <Button variant="outline" onClick={prev} disabled={step === 1} className="gap-1" data-testid="audit-btn-prev">
            <ChevronLeft size={16} /> Precedent
          </Button>
          {step < 6 ? (
            <Button onClick={next} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="audit-btn-next">
              Suivant <ChevronRight size={16} />
            </Button>
          ) : (
            <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="audit-btn-submit">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Envoi...</> : <><CheckCircle size={16} /> Envoyer la demande</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
